import { useMemo, useState } from "react";
import { Search, RefreshCw, Database, Loader2 } from "lucide-react";
import { formatDateTime } from "@/lib/testFormat";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "candidates", label: "Candidates" },
  { key: "reference", label: "Reference" },
];

export function ResultPill({ result }) {
  const styles = {
    PASS: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    FAIL: "bg-red-500/10 text-red-400 border-red-500/20",
    REFERENCE: "bg-sky-500/10 text-sky-400 border-sky-500/20",
    PENDING: "bg-zinc-800 text-zinc-400 border-white/10",
    "NO DATA": "bg-yellow-500/5 text-yellow-500 border-yellow-500/20",
  };
  const label = result === "REFERENCE" ? "REF" : result || "PENDING";
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${styles[result] || styles.PENDING}`}>
      {label}
    </span>
  );
}

export function TestPicker({ tests, loading, error, selectedId, onSelect, onRefresh }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tests.filter((t) => {
      if (filter === "reference" && !t.isReference) return false;
      if (filter === "candidates" && t.isReference) return false;
      if (!q) return true;
      return [t.id, t.name, t.rfid, t.testCenter].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [tests, query, filter]);

  return (
    <aside data-testid="test-picker" className="bg-zinc-900 border border-white/10 rounded-xl flex flex-col lg:max-h-[calc(100vh-140px)] lg:sticky lg:top-24">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400">Recorded Tests</span>
          <button
            type="button"
            data-testid="refresh-tests-btn"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh from Firebase"
            className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="test-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, ID, RFID"
            className="w-full bg-zinc-950 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500/50"
          />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`test-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
                filter === f.key ? "bg-yellow-500 text-black font-bold" : "bg-zinc-800 text-zinc-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-2">
        {loading && !tests.length ? (
          <div className="p-6 text-center text-sm text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-yellow-500" />
            Loading tests...
          </div>
        ) : error ? (
          <p role="alert" className="p-4 text-sm text-red-400">{error}</p>
        ) : !visible.length ? (
          <div className="p-6 text-center text-sm text-zinc-500">
            <Database className="w-5 h-5 mx-auto mb-2" />
            No tests found
          </div>
        ) : (
          visible.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`test-item-${t.id}`}
              onClick={() => onSelect(t.id)}
              className={`w-full text-left p-3 rounded-lg mb-1 border transition-colors ${
                selectedId === t.id
                  ? "bg-yellow-500/10 border-yellow-500/30"
                  : "border-transparent hover:bg-white/5"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-white truncate">{t.name}</span>
                <ResultPill result={t.result} />
              </div>
              <div className="flex items-center justify-between gap-2 mt-1 text-[11px] font-mono text-zinc-500">
                <span>{t.id}</span>
                <span>{t.sampleCount ?? 0} samples</span>
              </div>
              <div className="text-[11px] text-zinc-600 mt-0.5">{t.startMs ? formatDateTime(t.startMs) : "No start time"}</div>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}
